import { useCallback } from 'react';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IModel2D } from '../../../models/standard/Model2DModel';

export interface IModel2DActions {
  addModel2D: (model: IModel2D, spaceUuid?: string) => void;  
  setModel2D: (models: Array<IModel2D>) => void;
  deleteModel2D: (uuid: string) => void;
  updateModel2D: (updatedModel: IModel2D) => void;
  setModel2DRotation: (uuid: string, rotation: number) => void;
}

export const useModel2DActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IModel2DActions => {
  
  const addModel2D = useCallback(
    (newModel2D: IModel2D, spaceUuid?: string): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
  
        // 1. Try Adding to a specific Space
        if (spaceUuid && next.rooms) {
          let addedToSpace = false;
          
          const newRooms = next.rooms.map((room) => {
            if (!room.spaces) return room;
            
            let roomModified = false;
            const newSpaces = room.spaces.map((space) => {
              if (space.uuid === spaceUuid) {
                addedToSpace = true;
                roomModified = true;
                
                // Create a shallow copy of the space preserving the prototype (methods)
                const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                // Append the new model to this space's list
                newSpace.models2D = [...(space.models2D || []), newModel2D];
                return newSpace;
              }
              return space;
            });
  
            // If a space inside this room changed, we must clone the room too
            if (roomModified) {
              const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
              newRoom.spaces = newSpaces;
              return newRoom;
            }
            return room;
          });
  
          // If we found the space and added the item, return the new state here
          if (addedToSpace) {
            return { 
              ...next, 
              rooms: newRooms, 
              isSetByUndoRedo: false, 
              lastAction: EHistoryAction.MODEL2D_ADDED 
            };
          }
        }
  
        // 2. Fallback: Add to Root (if not added to a space)
        next.models2D = [...(next.models2D || []), newModel2D];
        
        return { 
          ...next, 
          isSetByUndoRedo: false, 
          lastAction: EHistoryAction.MODEL2D_ADDED 
        };
      });
    },
    [setBuilding],
  );

  const setModel2D = useCallback(
    (models: Array<IModel2D>): void => {
      setBuilding((prevBuilding) => ({ ...prevBuilding, isSetByUndoRedo: false, lastAction: EHistoryAction.MODEL2D_SET, models2D: models }));
    },
    [setBuilding],
  );

  const deleteModel2D = useCallback(
    (uuid: string): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Root
        if (next.models2D) {
            const idx = next.models2D.findIndex(m => m.uuid === uuid);
            if (idx !== -1) {
                const newArr = [...next.models2D];
                newArr.splice(idx, 1);
                next.models2D = newArr;
                found = true;
            }
        }

        // 2. Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map(room => {
                if (!room.spaces) return room;
                let roomChanged = false;
                const newSpaces = room.spaces.map(space => {
                    if (!space.models2D) return space;
                    const idx = space.models2D.findIndex(m => m.uuid === uuid);
                    if (idx !== -1) {
                        found = true; roomChanged = true;
                        const newArr = [...space.models2D];
                        newArr.splice(idx, 1);
                        const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                        newSpace.models2D = newArr;
                        return newSpace;
                    }
                    return space;
                });
                if (roomChanged) {
                    const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }
        if (!found) return prevBuilding;
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.MODEL2D_DELETED };
      });
    },
    [setBuilding],
  );

  const updateModel2D = useCallback(
    (updatedModel: IModel2D): void => {
      const targetUuid = updatedModel.uuid;
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Root
        if (next.models2D) {
            const idx = next.models2D.findIndex(m => m.uuid === targetUuid);
            if (idx !== -1) {
                const newArr = [...next.models2D];
                newArr[idx] = updatedModel;
                next.models2D = newArr;
                found = true;
            }
        }

        // 2. Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map(room => {
                if (!room.spaces) return room;
                let roomChanged = false;
                const newSpaces = room.spaces.map(space => {
                    if (!space.models2D) return space;
                    const idx = space.models2D.findIndex(m => m.uuid === targetUuid);
                    if (idx !== -1) {
                        found = true; roomChanged = true;
                        const newArr = [...space.models2D];
                        newArr[idx] = updatedModel;
                        const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                        newSpace.models2D = newArr;
                        return newSpace;
                    }
                    return space;
                });
                if (roomChanged) {
                    const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }
        if (!found) return prevBuilding;
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.MODEL2D_UPDATED };
      });
    },
    [setBuilding],
  );

  const setModel2DRotation = useCallback(
    (uuid: string, rotation: number): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Root
        if (next.models2D) {
            const idx = next.models2D.findIndex(m => m.uuid === uuid);
            if (idx !== -1) {
                const newArr = [...next.models2D];
                newArr[idx] = { ...newArr[idx], rotation };
                next.models2D = newArr;
                found = true;
            }
        }

        // 2. Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map(room => {
                if (!room.spaces) return room;
                let roomChanged = false;
                const newSpaces = room.spaces.map(space => {
                    if (!space.models2D) return space;
                    const idx = space.models2D.findIndex(m => m.uuid === uuid);
                    if (idx !== -1) {
                        found = true; roomChanged = true;
                        const newArr = [...space.models2D];
                        newArr[idx] = { ...newArr[idx], rotation };
                        const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                        newSpace.models2D = newArr;
                        return newSpace;
                    }
                    return space;
                });
                if (roomChanged) {
                    const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }
        if (!found) return prevBuilding;
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.MODEL2D_ROTATION_CHANGED };
      });
    },
    [setBuilding],
  );

  return { addModel2D, setModel2D, deleteModel2D, updateModel2D, setModel2DRotation };
};